import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Phone, Mail, MessageSquare, Save,
  CreditCard, Truck, Calendar, Hash, Tag, User, Car, FileText,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { apiClient, formatErr, STATUS_LABELS, STATUS_COLORS, CORRIERI } from "@/lib/api";
import PreventiviTab from "@/pages/admin/PreventiviTab";

const STATUSES = ["nuovo", "in_lavorazione", "bonifico_inviato", "spedito", "chiuso"];

export default function AdminQuoteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [bankDefault, setBankDefault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("preventivo");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qR, sR] = await Promise.all([
        apiClient.get(`/admin/quotes/${id}`),
        apiClient.get("/admin/settings"),
      ]);
      setQuote(qR.data);
      setBankDefault(sR.data);
    } catch (e) {
      toast.error(formatErr(e));
      nav("/admin");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await apiClient.patch(`/admin/quotes/${id}`, { status: newStatus });
      setQuote(data);
      toast.success(`Stato aggiornato: ${STATUS_LABELS[newStatus]}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveNotes = async (notes) => {
    try {
      const { data } = await apiClient.patch(`/admin/quotes/${id}`, { admin_notes: notes });
      setQuote(data);
      toast.success("Note salvate");
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading || !quote) {
    return (
      <AdminLayout title="Caricamento..." subtitle="Dettaglio">
        <div className="flex justify-center py-20">
          <Loader2 size={28} className="animate-spin text-[#E63946]" />
        </div>
      </AdminLayout>
    );
  }

  const formatDate = (iso) =>
    iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <AdminLayout
      title={quote.nome}
      subtitle={`Richiesta #${quote.id.slice(0, 8)}`}
      actions={
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 text-xs uppercase tracking-widest font-mono transition-colors bg-white"
        >
          <ArrowLeft size={12} /> Torna
        </Link>
      }
    >
      <div className="grid lg:grid-cols-12 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-5 space-y-4">
          {/* Status */}
          <div className="bg-white border border-zinc-200 p-5">
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-3">
              Stato Richiesta
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  data-testid={`status-btn-${s}`}
                  className={`px-3 py-2 text-[10px] uppercase tracking-widest font-mono font-bold transition-all ${
                    quote.status === s
                      ? STATUS_COLORS[s]
                      : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"
                  }`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Info card */}
          <div className="bg-white border border-zinc-200">
            <div className="px-5 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-widest font-mono text-[#E63946]">
              / Dati Cliente
            </div>
            <div className="p-5 space-y-3 text-sm">
              <InfoRow icon={User} label="Nome" value={quote.nome} />
              <InfoRow
                icon={Phone}
                label="Telefono"
                value={
                  <a href={`tel:${quote.telefono}`} className="hover:text-[#E63946]">
                    {quote.telefono}
                  </a>
                }
              />
              <InfoRow
                icon={Mail}
                label="Email"
                value={
                  quote.email ? (
                    <a href={`mailto:${quote.email}`} className="hover:text-[#E63946]">
                      {quote.email}
                    </a>
                  ) : "—"
                }
              />
              <InfoRow icon={Tag} label="Tipo Ricambio" value={quote.tipo_ricambio} highlight />
              <InfoRow icon={Hash} label="Codice / Targa" value={quote.codice_motore} mono />
              <InfoRow icon={Car} label="Marca / Modello" value={quote.marca_modello || "—"} />
              <InfoRow icon={Calendar} label="Ricevuto" value={formatDate(quote.created_at)} mono />
            </div>
            {quote.messaggio && (
              <div className="px-5 pb-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
                  <MessageSquare size={11} /> Messaggio
                </div>
                <div className="bg-zinc-50 border-l-2 border-[#E63946] p-3 text-sm leading-relaxed whitespace-pre-wrap text-zinc-700">
                  {quote.messaggio}
                </div>
              </div>
            )}
          </div>

          {/* Sent log */}
          {(quote.bonifico_sent_at || quote.tracking_sent_at || quote.customer_email_sent) && (
            <div className="bg-white border border-zinc-200 p-5">
              <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-3">
                Storia Email
              </div>
              <ul className="text-xs text-zinc-600 space-y-2 font-mono">
                {quote.customer_email_sent && (
                  <li>✓ Conferma ricezione inviata al cliente</li>
                )}
                {quote.bonifico_sent_at && (
                  <li>✓ Bonifico inviato — {formatDate(quote.bonifico_sent_at)}</li>
                )}
                {quote.tracking_sent_at && (
                  <li>
                    ✓ Tracking inviato — {quote.tracking_corriere} {quote.tracking_numero} — {formatDate(quote.tracking_sent_at)}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-7">
          <div className="bg-white border border-zinc-200">
            <div className="grid grid-cols-4 border-b border-zinc-200">
              <TabBtn current={tab} value="preventivo" onClick={setTab} icon={FileText}>
                Preventivo
              </TabBtn>
              <TabBtn current={tab} value="bonifico" onClick={setTab} icon={CreditCard}>
                Coordinate Bonifico
              </TabBtn>
              <TabBtn current={tab} value="tracking" onClick={setTab} icon={Truck}>
                Spedizione
              </TabBtn>
              <TabBtn current={tab} value="note" onClick={setTab} icon={MessageSquare}>
                Note Interne
              </TabBtn>
            </div>

            <div className="p-6">
              {tab === "preventivo" && <PreventiviTab quote={quote} />}
              {tab === "bonifico" && (
                <BonificoForm quote={quote} bankDefault={bankDefault} onSent={load} />
              )}
              {tab === "tracking" && <TrackingForm quote={quote} onSent={load} />}
              {tab === "note" && <NotesForm quote={quote} onSave={saveNotes} />}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function InfoRow({ icon: Icon, label, value, mono, highlight }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-zinc-400 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</div>
        <div
          className={`${mono ? "font-mono text-sm" : "text-sm"} ${
            highlight ? "text-[#E63946] font-bold" : "text-zinc-900"
          }`}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ current, value, onClick, icon: Icon, children }) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      data-testid={`tab-${value}`}
      className={`flex items-center justify-center gap-2 px-4 py-4 text-xs uppercase tracking-widest font-mono transition-colors ${
        active
          ? "bg-[#E63946] text-white"
          : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50"
      }`}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

function BonificoForm({ quote, bankDefault, onSent }) {
  const [form, setForm] = useState({
    intestatario: bankDefault?.intestatario || "",
    iban: bankDefault?.iban || "",
    banca: bankDefault?.banca || "",
    bic: bankDefault?.bic || "",
    causale: `Pagamento ricambio - ${quote.tipo_ricambio} - ${quote.nome}`,
    importo: "",
    note: "",
  });
  const [sending, setSending] = useState(false);
  const hasEmail = !!quote.email;

  const send = async () => {
    if (!hasEmail) { toast.error("Il cliente non ha fornito un'email."); return; }
    if (!form.intestatario || !form.iban || !form.causale) {
      toast.error("Compila intestatario, IBAN e causale."); return;
    }
    setSending(true);
    try {
      await apiClient.post(`/admin/quotes/${quote.id}/send-bonifico`, form);
      toast.success("Email coordinate bonifico inviata!");
      onSent();
    } catch (e) { toast.error(formatErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      {!hasEmail && <EmailMissingWarning />}
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Intestatario *" value={form.intestatario} onChange={(v) => setForm({ ...form, intestatario: v })} testid="bonifico-intestatario" />
        <Field label="IBAN *" value={form.iban} onChange={(v) => setForm({ ...form, iban: v })} mono testid="bonifico-iban" />
        <Field label="Banca" value={form.banca} onChange={(v) => setForm({ ...form, banca: v })} testid="bonifico-banca" />
        <Field label="BIC / SWIFT" value={form.bic} onChange={(v) => setForm({ ...form, bic: v })} mono testid="bonifico-bic" />
        <Field label="Causale *" value={form.causale} onChange={(v) => setForm({ ...form, causale: v })} className="md:col-span-2" testid="bonifico-causale" />
        <Field label="Importo (€)" value={form.importo} onChange={(v) => setForm({ ...form, importo: v })} placeholder="es. 1850,00" testid="bonifico-importo" />
      </div>
      <Field label="Note aggiuntive" value={form.note} onChange={(v) => setForm({ ...form, note: v })} textarea testid="bonifico-note" />
      <button
        onClick={send}
        disabled={sending || !hasEmail}
        data-testid="bonifico-send"
        className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs transition-colors"
      >
        {sending ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
        {sending ? "Invio..." : "Invia Coordinate al Cliente"}
      </button>
    </div>
  );
}

function TrackingForm({ quote, onSent }) {
  const [form, setForm] = useState({
    corriere: quote.tracking_corriere || "",
    numero: quote.tracking_numero || "",
    note: "",
  });
  const [sending, setSending] = useState(false);
  const hasEmail = !!quote.email;

  const send = async () => {
    if (!hasEmail) { toast.error("Il cliente non ha fornito un'email."); return; }
    if (!form.corriere || !form.numero) {
      toast.error("Inserisci corriere e numero tracking."); return;
    }
    setSending(true);
    try {
      await apiClient.post(`/admin/quotes/${quote.id}/send-tracking`, form);
      toast.success("Email tracking inviata!");
      onSent();
    } catch (e) { toast.error(formatErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      {!hasEmail && <EmailMissingWarning />}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
            Corriere *
          </label>
          <select
            value={form.corriere}
            onChange={(e) => setForm({ ...form, corriere: e.target.value })}
            data-testid="tracking-corriere"
            className="w-full bg-white border border-zinc-300 px-4 py-3 text-zinc-900 focus:outline-none focus:border-[#E63946] transition-colors text-sm"
          >
            <option value="">Seleziona...</option>
            {CORRIERI.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <Field label="Numero tracking *" value={form.numero} onChange={(v) => setForm({ ...form, numero: v })} mono placeholder="es. AB123456789IT" testid="tracking-numero" />
      </div>
      <Field label="Note aggiuntive" value={form.note} onChange={(v) => setForm({ ...form, note: v })} textarea testid="tracking-note" />
      <button
        onClick={send}
        disabled={sending || !hasEmail}
        data-testid="tracking-send"
        className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs transition-colors"
      >
        {sending ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
        {sending ? "Invio..." : "Invia Tracking al Cliente"}
      </button>
    </div>
  );
}

function NotesForm({ quote, onSave }) {
  const [notes, setNotes] = useState(quote.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(notes);
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <Field label="Note interne (non inviate al cliente)" value={notes} onChange={setNotes} textarea rows={8} testid="notes-textarea" />
      <button
        onClick={save}
        disabled={saving}
        data-testid="notes-save"
        className="inline-flex items-center gap-3 px-6 py-3.5 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-xs transition-colors"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Salva Note
      </button>
    </div>
  );
}

function Field({ label, value, onChange, mono, className = "", placeholder = "", textarea = false, rows = 3, testid }) {
  const base =
    "w-full bg-white border border-zinc-300 px-4 py-3 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#E63946] transition-colors text-sm";
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-widest font-mono text-zinc-500 mb-2">
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          rows={rows}
          placeholder={placeholder}
          className={`${base} ${mono ? "font-mono" : ""} resize-none`}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className={`${base} ${mono ? "font-mono" : ""}`}
        />
      )}
    </div>
  );
}

function EmailMissingWarning() {
  return (
    <div className="bg-amber-50 border border-amber-300 px-4 py-3 text-xs text-amber-900">
      ⚠️ Questo cliente non ha fornito un&apos;email — l&apos;invio automatico è disabilitato. Contattalo
      direttamente via telefono o WhatsApp.
    </div>
  );
}
